import { useFormik } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { UserIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import { userApi } from '../../../repositories/user';
import { handleError } from '../../../utils/errorHandler';
import EditPasswordModal from '../../../components/modal/EditPasswordModal';
import { Skeleton, SkeletonFields } from '../../ui/skeleton';
import SettingsPanel from '../../ui/SettingsPanel';
import { useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearAuth } from '../../../store/slices/authSlice';
import { useAppSelector } from '../../../hooks/redux';
import { setUser } from '../../../store/slices/userSlice';
import Button from '../../ui/Button';
import { swalColors } from '../../../utils/swal';

interface ProfileData {
  id: number;
  fullName: string;
  email: string;
  role: string;
  memberSince: string;
  lastLogin: string;
  associatedLabel: string;
  profilePicture: string | null;
  organization?: {
    id: string;
    name: string;
  };
}

interface PasswordFormValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const validationSchema = Yup.object({
  fullName: Yup.string().required('Full name is required'),
});

const inputClass =
  'w-full p-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500';

/*
 * Email, Role and Label are not editable here. They were styled exactly like
 * the editable Full Name field, so the form invited edits it silently
 * discarded - a muted fill and default cursor say "display only" instead.
 */
const readOnlyInputClass =
  'w-full p-2 text-sm border rounded-md outline-none cursor-default text-neutral-600 bg-neutral-50 border-neutral-200';

const Profile: React.FC = () => {
  const user = useAppSelector((state) => state.user);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loading = user.loading || user.id === null;

  const memberSinceDate = user.createdAt ? new Date(user.createdAt) : null;
  const lastLoginDate = user.lastLogin ? new Date(user.lastLogin) : null;
  const profileData: ProfileData = {
    id: user.id || 0,
    fullName: user.fullName || '',
    email: user.email || '',
    role: user.role || 'basic',
    memberSince: memberSinceDate ? format(memberSinceDate, 'MMMM d, yyyy') : '',
    lastLogin: lastLoginDate
      ? format(lastLoginDate, 'MMMM d, yyyy, h:mm a')
      : 'Never logged in',
    associatedLabel: user.Label?.name || 'No label associated',
    profilePicture: user.profilePicture || null,
    organization: user.organization || undefined,
  };

  const formik = useFormik({
    initialValues: {
      fullName: profileData.fullName,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (isFormChanged) {
          const response = await userApi.updateProfile({
            fullName: values.fullName,
          });
          if (!response?.data?.success) {
            throw new Error(response?.data?.error || 'Failed to update profile');
          }
          dispatch(setUser({
            fullName: values.fullName,
          }));
          setIsFormChanged(false);
          Swal.fire({
            title: 'Success!',
            text: response.data.message || 'Profile updated successfully.',
            icon: 'success',
            confirmButtonText: 'OK',
          });
        } else {
          Swal.fire({
            title: 'No changes',
            text: 'No changes were made to your profile.',
            icon: 'info',
            confirmButtonText: 'OK',
          });
        }
      } catch (error) {
        handleError(error);
      }
    },
  });

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        Swal.fire({
          title: 'Error',
          text: 'Please select a valid image file (JPG, PNG, GIF, BMP, WEBP, SVG).',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire({
          title: 'Error',
          text: 'File size exceeds 10MB limit.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }
      setSelectedFile(file);
      setImageError(false);
    } else {
      Swal.fire({
        title: 'Error',
        text: 'No file selected.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      Swal.fire({
        title: 'Error',
        text: 'Please select an image to upload.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    try {
      Swal.fire({
        title: 'Uploading...',
        text: 'Please wait while your image is being uploaded.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const formData = new FormData();
      formData.append('profilePicture', selectedFile);

      const response = await userApi.uploadProfilePicture(formData);
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Failed to upload profile picture');
      }

      dispatch(setUser({
        profilePicture: response.data.data.profilePicture || null,
      }));

      setSelectedFile(null);
      setImageError(false);

      Swal.fire({
        title: 'Success!',
        text: response.data.message || 'Profile picture uploaded successfully.',
        icon: 'success',
        confirmButtonText: 'OK',
      });
    } catch (error:any) {
      Swal.close();
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to upload profile picture.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleFormChange = (e: ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    setIsFormChanged(true);
  };

  const handlePasswordSubmit = async (
    values: PasswordFormValues,
    { setSubmitting, resetForm }: any
  ) => {
    try {
      const response = await userApi.updatePassword({
        oldPassword: values.oldPassword,
        password: values.newPassword,
      });
      if (!response?.data?.success) {
        throw new Error(response?.data?.error || 'Failed to update password');
      }
      Swal.fire({
        title: 'Success!',
        text: response.data.message || 'Password updated successfully.',
        icon: 'success',
        confirmButtonText: 'OK',
      });
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      handleError(error);
    } finally {
      setSubmitting(false);
    }
  };


  const handleDeleteAccount = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalColors.danger,
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        Swal.fire({
          title: 'Deleting...',
          text: 'Please wait while your account is being deleted.',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const response = await userApi.deleteAccount(profileData.id);
        if (!response?.data?.success) {
          throw new Error(response?.data?.error || 'Failed to delete account');
        }

        localStorage.clear();
        Swal.fire({
          title: 'Deleted!',
          text: response.data.message || 'Your account has been deleted.',
          icon: 'success',
          confirmButtonText: 'OK',
        });
        dispatch(clearAuth());
        navigate('/auth/login');
      } catch (error) {
        Swal.close();
        handleError(error);
      }
    }
  };

  if (loading) {
    return (
      <SettingsPanel loading>
        {/* Mirrors the real layout below - avatar row, then a two-column grid -
            so content lands in place instead of shifting when it arrives. */}
        <Skeleton className="w-32 h-5 mb-4" />
        <div className="flex flex-col items-center gap-4 pb-6 mb-6 border-b sm:flex-row border-neutral-100">
          <Skeleton className="w-20 h-20 rounded-full shrink-0" />
          <Skeleton className="w-32 h-10 rounded-md" />
        </div>
        <Skeleton className="w-full h-20 mb-6 rounded-lg" />
        <SkeletonFields count={4} columns={2} />
        <Skeleton className="w-40 h-10 mt-6 rounded-md" />
      </SettingsPanel>
    );
  }

  return (
    <SettingsPanel
      title="User Profile"
      description="Your account details and sign-in settings"
      divided
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="flex flex-col">
          {/*
            Avatar and its controls sit side by side rather than stacked and
            centred: the rest of the form is left-aligned, so a centred column
            on top read as a different page. Buttons use the app's standard
            rounded-md treatment - these were the only rounded-full pills in it.
          */}
          <div className="flex flex-col items-center gap-4 pb-6 mb-6 border-b sm:flex-row sm:items-center border-neutral-100">
            <div className="flex items-center justify-center w-20 h-20 overflow-hidden rounded-full shrink-0 bg-primary-100">
              {(profileData.profilePicture && !imageError) ? (
                <img
                  src={profileData.profilePicture}
                  alt="Profile"
                  className="object-cover w-full h-full"
                  onError={() => setImageError(true)}
                />
              ) : (
                <UserIcon className="w-10 h-10 text-primary-700" />
              )}
            </div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/bmp,image/webp,image/svg+xml"
              onChange={handleFileChange}
              id="profilePictureInput"
              hidden
            />
            <div className="text-center sm:text-left">
              <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
                <label
                  htmlFor="profilePictureInput"
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium transition-colors bg-white border rounded-md cursor-pointer text-primary-700 border-primary-300 hover:bg-primary-50 focus-within:outline-none focus-within:border-primary-700"
                >
                  Select Image
                </label>
                {selectedFile && (
                  <Button
                    type="button"
                    onClick={handleUpload}
                  >
                    Upload Image
                  </Button>
                )}
              </div>
              <p className="mt-2 text-xs text-neutral-500">
                {selectedFile
                  ? `${selectedFile.name} selected - click Upload Image to save.`
                  : 'JPG, PNG, GIF, BMP, WEBP or SVG. Up to 10MB.'}
              </p>
            </div>
          </div>

          {/*
            Read-only account facts, paired on one row. They were stacked
            full-width like editable fields, which gave two short dates the same
            visual weight as the form below them.
          */}
          <dl className="grid grid-cols-1 gap-4 p-4 mb-6 rounded-lg sm:grid-cols-2 bg-neutral-50">
            <div>
              <dt className="mb-1 text-xs font-medium text-neutral-700 sm:text-sm">Member Since</dt>
              <dd className="text-xs text-neutral-600 sm:text-sm">{profileData.memberSince}</dd>
            </div>
            <div>
              <dt className="mb-1 text-xs font-medium text-neutral-700 sm:text-sm">Last Login</dt>
              <dd className="text-xs text-neutral-600 sm:text-sm">{profileData.lastLogin}</dd>
            </div>
          </dl>

          {/* Two columns from sm up: at full width a single column of stretched
              inputs left most of the card empty. */}
          <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2 sm:mb-6">
            <div>
              <label htmlFor="profile-full-name" className="block mb-1 text-xs font-medium text-neutral-700 sm:text-sm">Full Name</label>
              <input
                id="profile-full-name"
                type="text"
                name="fullName"
                value={formik.values.fullName}
                onChange={handleFormChange}
                onBlur={formik.handleBlur}
                className={inputClass}
              />
              {formik.touched.fullName && formik.errors.fullName ? (
                <p className="mt-1 text-xs text-danger-600">{formik.errors.fullName}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="profile-email" className="block mb-1 text-xs font-medium text-neutral-700 sm:text-sm">Email Address</label>
              <input
                id="profile-email"
                type="email"
                value={profileData.email}
                className={readOnlyInputClass}
                readOnly
              />
            </div>

            <div>
              <label htmlFor="profile-role" className="block mb-1 text-xs font-medium text-neutral-700 sm:text-sm">Role</label>
              <input
                id="profile-role"
                type="text"
                value={profileData.role ==="basic" ? "user":profileData.role}
                className={readOnlyInputClass}
                readOnly
              />
            </div>

            <div>
              <label htmlFor="profile-label" className="block mb-1 text-xs font-medium text-neutral-700 sm:text-sm">
                {profileData.role === 'dsp' && profileData.organization ? 'Associated Organization' : 'Associated Label'}
              </label>
              <input
                id="profile-label"
                type="text"
                value={profileData.role === 'dsp' && profileData.organization
                  ? profileData.organization.name
                  : profileData.associatedLabel}
                className={readOnlyInputClass}
                readOnly
              />
            </div>
          </div>

          {/*
            Save is the primary action, so it leads rather than sitting full
            width under two secondary buttons. Change Password is a routine
            action and stays beside it; Delete Account is separated out below.
          */}
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              type="submit"
              disabled={!isFormChanged}
            >
              Save Changes
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setIsModalOpen(true)}
            >
              Change Password
            </Button>
          </div>

          {/* Irreversible, so it is pulled out of the row of routine actions
              and given its own labelled block rather than sitting a tab-stop
              away from Save. */}
          <div className="flex flex-col gap-3 pt-6 mt-8 border-t sm:flex-row sm:items-center sm:justify-between border-neutral-100">
            <div>
              <p className="text-sm font-medium text-black">Delete account</p>
              <p className="mt-0.5 text-xs text-neutral-500">
                Permanently removes your account and its data. This cannot be undone.
              </p>
            </div>
            <Button
              variant="dangerOutline"
              type="button"
              onClick={handleDeleteAccount}
              className="shrink-0"
            >
              Delete Account
            </Button>
          </div>
        </div>
      </form>

      <EditPasswordModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handlePasswordSubmit}
      />
    </SettingsPanel>
  );
};

export default Profile;